import { isNonProductionHost } from 'utils/environment'

// Tenants que TODAVÍA ven la vista legacy de Hallazgos/Inbox. La lista explícita es
// ésta y el default es la experiencia NUEVA (rediseño STORE-170): al revés de como
// arrancó la migración.
//
// El default se invirtió porque la lista de migrados sólo sirve para los tenants que
// existían cuando empezó el rediseño. Uno que se da de alta hoy nunca conoció la vista
// vieja, así que abrirlo en legacy no le ahorra ningún cambio: le agrega uno. Y como
// nadie se acuerda de tocar este archivo al crear un tenant, el alta silenciosamente lo
// mandaba a la vista que estamos sacando. Pasó con `claro-cr` (alta 25/08/2026).
//
// Los cuatro de acá son los que existían antes del rediseño y todavía no migraron.
// `movistar-ar` es el único con usuarios reales; los otros tres no tienen tráfico, pero
// se quedan igual: este cambio es sobre el default de los tenants nuevos, no una
// migración encubierta de los viejos. Al migrar uno, sacarlo de la lista.
//
// Los que ya migraron (`agroenergia-ar`, `megamix-ar`, `weiss-ar`, `wts-pe`,
// `combustibles-ar`) no necesitan estar en ninguna lista: los cubre el default.
const LEGACY_INBOX_HOSTS = ['movistar-ar', 'retail-ar', 'skel-pe', 'dimanche-ar']

// Tenants EN CONVIVENCIA: ven el banner para probar la vista nueva y, al pasarse,
// el tutorial. En producción es una lista explícita, porque la convivencia se habilita
// tenant por tenant: encenderla de golpe en todos los que todavía no migraron, Movistar
// Perú incluido, es lo contrario de lo que se busca para una primera salida.
//
// La convivencia es sólo para el que YA usa la vista vieja y hay que mudar. Un tenant
// nuevo no tiene de dónde mudarse: entra directo a la vista nueva por el default y no
// ve banner ni tutorial.
//
// `demo-ar` es el primero a propósito. Es el tenant de demos y pruebas en
// producción, así que es donde el equipo puede recorrer el camino completo (banner,
// tutorial, ida y vuelta entre vistas) sobre datos reales y con Clarity midiendo,
// que sólo se inicializa en producción. Mientras esté acá arranca en la vista vieja,
// como cualquier tenant que todavía no migró.
//
// Al habilitar un tenant, sumarlo acá. Al terminar su convivencia, sacarlo: queda con
// la vista nueva por el default y el banner desaparece solo, sin dejar hueco.
//
// `movistar-pe` estuvo acá del 24/08 al 31/08 y salió con su migración: es el caso que
// motivó toda la convivencia. Durante esa semana probaron la vista nueva 13 usuarios de
// 150 activos, así que el 91% llega a la vista nueva sin haberla visto nunca. Por eso
// conserva el tutorial después de migrar: ver POST_MIGRATION_ONBOARDING_HOSTS.
const OPT_IN_INBOX_HOSTS = ['demo-ar']

// Tenants YA MIGRADOS que igual siguen viendo el tutorial de la vista nueva.
//
// Al salir de la convivencia un tenant deja de ser opt-in, y con eso el tutorial dejaría
// de abrirse (`deberiaAbrirse()` en InboxOnboarding). Para el que migró habiendo probado
// la vista nueva eso está bien, ya la conoce. Para Movistar Perú no: la semana de
// convivencia la usaron 13 personas de 150, así que el resto se encuentra la interfaz
// nueva de un día para el otro y el tutorial es lo único que se la explica.
//
// Es una lista y no el complemento de OPT_IN_INBOX_HOSTS a propósito. Los tenants que ya
// estaban en la vista nueva (`agroenergia-ar`, `megamix-ar`, `weiss-ar`, `wts-pe`,
// `combustibles-ar`, `claro-cr` y toda alta nueva) tampoco vieron nunca el tutorial,
// porque nunca estuvieron en convivencia. Sacar la guarda en vez de listar acá se los
// dispararía a todos de golpe en el mismo deploy, que es el accidente que ya se evitó el
// 19/08 con el banner, entrando por otra puerta.
//
// Se saca un tenant de acá cuando su tanda de migración ya pasó. No hace falta esperar a
// que todos lo cierren: el que lo cerró tiene la versión guardada y no lo vuelve a ver.
const POST_MIGRATION_ONBOARDING_HOSTS = ['movistar-pe']

// QA en local/staging: el subdominio de Agro es de prod, no se reproduce en localhost.
// localStorage.__force_inbox = 'new' | 'legacy' fuerza una vista (requiere reload).
// Es lo único que permite forzar la vista legacy en un tenant que ya migró, porque
// para esos el toggle de convivencia no se muestra.
const readForcedInbox = () => {
    try {
        return localStorage.getItem('__force_inbox')
    } catch (e) {
        return null
    }
}

// Elegir una vista desde el banner apaga el override. Sin esto, el que alguna vez probó
// con el toggle de QA queda con la bandera puesta para siempre, y como se lee ANTES que
// la preferencia, el banner escribe la elección y nadie la mira: se toca "Probar" o
// "Volver a la anterior" y no pasa nada, sin error ni pista de por qué.
//
// El override existe para forzar una vista donde no hay control; si el usuario está
// usando el control, su elección es más nueva y más explícita que la bandera de QA.
const clearForcedInbox = () => {
    try {
        localStorage.removeItem('__force_inbox')
    } catch (e) {
        // sin localStorage no hay override que limpiar
    }
}

// Preferencia del usuario en tenants en convivencia. Objeto y no booleano suelto
// porque el flag del tutorial viaja con la elección de vista: el tutorial se dispara
// al cambiar de vista, así que son el mismo registro. Guardar la VERSIÓN vista (y no
// un `true`) permite volver a mostrarlo en un release futuro sin resetear nada.
//
// Vive en el navegador porque hoy no existe un almacén de preferencias por usuario en
// el backend: `user.parameters` viene de PARAMETERS_TENANT y es del tenant, o sea
// compartido por todos sus usuarios. Si Aicon expone un campo en /account/me, este
// módulo es el único lugar a tocar.
const PREFERENCE_KEY = 'inbox_view_preference'

// Copia en memoria de lo último que se escribió. Cubre el caso de localStorage no
// disponible (incógnito con storage bloqueado, política del navegador): sin esto el
// toggle no solo no persistía, directamente no cambiaba de vista, porque la lectura
// siguiente devolvía el valor viejo y el gate volvía a montar el mismo árbol.
let memoryPreference = null

// Lo último leído, ya parseado. La preferencia se consulta muchísimo más de lo que se
// escribe: `inventoryStatusColor` es un Proxy que resuelve la paleta en CADA acceso, y
// cada fila de la tabla de tareas lo toca dos veces, así que sin esto una tabla de cien
// filas hace doscientos `JSON.parse` por render. La caché no puede quedar vieja porque
// solo hay dos formas de que el valor cambie, y las dos la invalidan: `writePreference`
// en esta pestaña, y el evento `storage` cuando escribe otra.
let cachedPreference = null

const readPreference = () => {
    if (cachedPreference) return cachedPreference

    try {
        const raw = localStorage.getItem(PREFERENCE_KEY)
        if (!raw) {
            cachedPreference = memoryPreference || {}
            return cachedPreference
        }
        const parsed = JSON.parse(raw)
        cachedPreference = parsed && typeof parsed === 'object' ? parsed : memoryPreference || {}
        return cachedPreference
    } catch (e) {
        // localStorage no disponible o JSON corrupto: vale lo de esta sesión, si hay
        cachedPreference = memoryPreference || {}
        return cachedPreference
    }
}

const writePreference = (patch) => {
    const next = { ...readPreference(), ...patch }
    memoryPreference = next
    cachedPreference = next
    try {
        localStorage.setItem(PREFERENCE_KEY, JSON.stringify(next))
    } catch (e) {
        // Sin localStorage la preferencia dura lo que dura la pestaña, pero funciona
    }
}

// Suscripción al cambio de vista. Existe para que cambiar de vista NO recargue la
// página: los gates leen `isNewInboxTenant()` en render, y sin avisarle a React que la
// respuesta cambió se quedarían mostrando el árbol anterior.
//
// Set y no array: desmontar un suscriptor lo borra por identidad, sin recorrer.
const viewListeners = new Set()

export const subscribeInboxView = (listener) => {
    viewListeners.add(listener)
    return () => viewListeners.delete(listener)
}

// Un listener que explota no puede dejar a los demás sin enterarse: el que quedó sin
// avisar seguiría dibujando la vista vieja al lado de la nueva.
const notifyInboxViewChange = () => {
    viewListeners.forEach((listener) => {
        try {
            listener()
        } catch (e) {
            // no-op
        }
    })
}

// El backoffice se usa con varias pestañas abiertas. Cambiar la vista en una tiene que
// llegar a las otras: `useSyncExternalStore` exige que toda variación del snapshot venga
// acompañada de una notificación, y sin esto las demás pestañas se quedarían mostrando
// el árbol anterior hasta un render incidental.
//
// El evento `storage` solo lo reciben las OTRAS pestañas, nunca la que escribió, así que
// no duplica el aviso local que ya hace `setInboxViewPreference`.
if (typeof window !== 'undefined') {
    window.addEventListener('storage', (event) => {
        if (event.key !== PREFERENCE_KEY) return
        // La copia en memoria quedó vieja: la escribió otra pestaña.
        memoryPreference = null
        cachedPreference = null
        notifyInboxViewChange()
    })
}

const matchesHost = (list, host) => list.some((h) => host === h || host.startsWith(`${h}.`))

// El tenant está en convivencia: hay que mostrarle el banner de cambio de vista.
//
// Fuera de producción la convivencia vale para todo host que no esté en legacy puro.
// Es lo que hace que localhost, staging y los tunnels de QA sirvan para probarla sin
// anotar cada host en una lista, y es lo que mantiene a staging arrancando en la vista
// vieja: ahí el equipo recorre el camino completo del que todavía tiene que migrar.
//
// En producción manda OPT_IN_INBOX_HOSTS, que se habilita tenant por tenant. Los que ya
// migraron y los tenants nuevos se quedan con la vista nueva y no ven ningún banner.
export const isInboxOptInTenant = () => {
    const host = window.location.hostname

    if (isNonProductionHost()) {
        return !matchesHost(LEGACY_INBOX_HOSTS, host)
    }

    return matchesHost(OPT_IN_INBOX_HOSTS, host)
}

// El tenant ya migró pero conserva el tutorial. Sólo lo consulta `deberiaAbrirse()`: no
// habilita el banner ni ningún camino de vuelta a la vista vieja, que es justamente lo
// que la migración vino a cerrar.
//
// Fuera de producción devuelve false y no hace falta que devuelva otra cosa: ahí todo
// host que no sea legacy puro ya es opt-in, así que el tutorial abre por esa vía.
export const conservaOnboardingPostMigracion = () => {
    if (isNonProductionHost()) return false

    return matchesHost(POST_MIGRATION_ONBOARDING_HOSTS, window.location.hostname)
}

// 'new' | 'legacy'. Sin elección previa arranca en legacy: en convivencia el default
// es lo que el usuario ya conoce, y pasar a la vista nueva es una decisión suya.
export const readInboxViewPreference = () => (readPreference().view === 'new' ? 'new' : 'legacy')

// Momento del último cambio de vista. Lo consultan las dos pantallas de Tareas para
// distinguir por qué se están montando: alternar vista remonta el árbol igual que una
// navegación, pero no es una navegación, y la lista no tiene por qué empezar de cero.
//
// Es una marca temporal y no una bandera de un solo uso porque en desarrollo React monta,
// desmonta y vuelve a montar: consumirla dejaría al segundo montaje creyendo que llegó
// navegando.
let ultimoCambioDeVista = 0

const VENTANA_CAMBIO_DE_VISTA_MS = 1500

export const vieneDeCambioDeVista = () => Date.now() - ultimoCambioDeVista < VENTANA_CAMBIO_DE_VISTA_MS

// El cambio de vista pasa por un fundido: la que se va se apaga, el árbol se reemplaza
// con la pantalla ya cubierta, y la nueva se enciende. Sin esto el reemplazo ocurre en
// un fotograma y se lee como un parpadeo, no como una transición.
//
// El apagado es más corto que el encendido a propósito: sacar algo puede ser rápido
// porque el usuario ya sabe qué había, pero lo que llega necesita tiempo para leerse.
export const FUNDIDO_SALIDA_MS = 220
export const FUNDIDO_ENTRADA_MS = 420

// Cuánto tarda el cambio completo, de principio a fin. Lo usa el tutorial para esperar
// su turno en vez de aparecer encima del fundido.
export const FUNDIDO_TOTAL_MS = FUNDIDO_SALIDA_MS + FUNDIDO_ENTRADA_MS

const fadeListeners = new Set()

export const subscribeInboxFade = (listener) => {
    fadeListeners.add(listener)
    return () => fadeListeners.delete(listener)
}

const notifyFade = (fase) => {
    fadeListeners.forEach((listener) => {
        try {
            listener(fase)
        } catch (e) {
            // no-op
        }
    })
}

const prefiereMenosMovimiento = () => {
    try {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches
    } catch (e) {
        return false
    }
}

export const setInboxViewPreference = (view) => {
    clearForcedInbox()

    const aplicar = () => {
        ultimoCambioDeVista = Date.now()
        writePreference({ view: view === 'new' ? 'new' : 'legacy' })
        notifyInboxViewChange()
    }

    // Sin nadie que dibuje el velo el cambio no puede taparse, y hacerlo esperar igual
    // solo agregaría un retraso sin nada a cambio. Mismo criterio con quien pidió menos
    // movimiento: ahí el cambio instantáneo es la versión accesible, no una degradación.
    if (!fadeListeners.size || prefiereMenosMovimiento()) {
        aplicar()
        return
    }

    notifyFade('salida')

    // El árbol se reemplaza con el velo arriba del todo, así que el momento feo (la
    // vista vieja desapareciendo y los esqueletos de la nueva apareciendo) queda del
    // otro lado del telón.
    setTimeout(() => {
        aplicar()
        notifyFade('entrada')
    }, FUNDIDO_SALIDA_MS)
}

// Versión del tutorial que el usuario ya vio. 0 = nunca lo vio.
export const readOnboardingSeenVersion = () => {
    const seen = Number(readPreference().onboardingSeen)
    return Number.isFinite(seen) ? seen : 0
}

export const setOnboardingSeenVersion = (version) => writePreference({ onboardingSeen: version })

// Tocar "Probar" pide el tutorial explícitamente, y eso pesa más que haberlo visto:
// el que vuelve a la vista nueva después de dejarla suele estar reintentando, no
// repitiendo. Por eso el botón levanta esta bandera y el tutorial sale igual, aunque
// `onboardingSeen` ya esté en la versión actual.
//
// Es una bandera de un solo uso y no un flag de "mostrar siempre": el gate se evalúa
// en cada montaje del Inbox, así que sin consumirla el tutorial reaparecería en cada
// navegación a Tareas.
// Los suscriptores son para el caso en que el tutorial YA está montado: el botón de la
// barra de estado lo pide sin cambiar de vista, y el componente decide si abrirse
// leyendo la bandera en su primer render, que a esa altura ya pasó. Cuando el pedido
// viene del botón "Probar" no hay ningún suscriptor todavía (el tutorial se monta
// recién después del cambio de vista) y ahí la que manda es la lectura inicial.
const onboardingListeners = new Set()

export const subscribeOnboardingRequest = (listener) => {
    onboardingListeners.add(listener)
    return () => onboardingListeners.delete(listener)
}

export const requestOnboarding = () => {
    writePreference({ onboardingPending: true })
    onboardingListeners.forEach((listener) => {
        try {
            listener()
        } catch (e) {
            // Un suscriptor que explota no puede dejar sin abrir al resto.
        }
    })
}

export const isOnboardingPending = () => readPreference().onboardingPending === true

export const clearOnboardingPending = () => writePreference({ onboardingPending: false })

// El banner de invitación se puede cerrar, pero el descarte se honra 30 días y después
// vuelve a ofrecerse. Por eso se guarda el momento del descarte y no un booleano: con un
// `true` el que cierra sin leer no migra nunca, y la ventana de convivencia es corta.
const DISMISS_DAYS = 30

export const isInviteBannerDismissed = () => {
    const at = Number(readPreference().bannerDismissedAt)
    if (!Number.isFinite(at) || at <= 0) return false
    return Date.now() - at < DISMISS_DAYS * 24 * 60 * 60 * 1000
}

export const dismissInviteBanner = () => writePreference({ bannerDismissedAt: Date.now() })

// La invitación entra animada la primera vez que alguien la ve EN CADA SESIÓN, o sea al
// abrir el primer hallazgo del día. Es un elemento nuevo en una pantalla que la gente ya
// conoce de memoria: sin el movimiento inicial se lee como parte del decorado y nadie la
// registra. Repetirlo en cada hallazgo sería lo contrario, un tic; una sola vez en la
// vida del navegador tampoco alcanza, porque el que no la registró ese día no la vuelve
// a ver aparecer nunca más, y la ventana de convivencia es corta.
//
// `sessionStorage` y no la preferencia: dura lo que dura la pestaña. Sobrevive a una
// recarga, que no es volver a entrar, y arranca de nuevo en la sesión siguiente.
const INVITE_ANNOUNCED_KEY = 'inbox_banner_announced'

// Espejo en memoria para cuando `sessionStorage` está bloqueado (incógnito estricto,
// política del navegador): ahí la marca dura lo que dura la carga de la página, que
// alcanza para lo que más molesta, que la barra se re-anime al abrir el segundo hallazgo.
let inviteAnnouncedEnMemoria = false

export const isInviteBannerAnnounced = () => {
    if (inviteAnnouncedEnMemoria) return true

    try {
        return sessionStorage.getItem(INVITE_ANNOUNCED_KEY) === '1'
    } catch (e) {
        return false
    }
}

export const markInviteBannerAnnounced = () => {
    inviteAnnouncedEnMemoria = true

    try {
        sessionStorage.setItem(INVITE_ANNOUNCED_KEY, '1')
    } catch (e) {
        // sin sessionStorage la marca vale para esta carga de la página
    }
}

// Cerrar sesión reinicia el anuncio: el que vuelve a entrar arranca una sesión nueva y
// ahí tiene que pasar lo mismo que la primera vez. Sin esto, quien cierra sesión y
// vuelve a entrar en la misma pestaña no ve aparecer nada, porque `sessionStorage`
// sobrevive al logout: `localStorage.clear()` no lo toca.
//
// El espejo en memoria se baja también, y no por las dudas: cerrar sesión no recarga
// la página, así que este módulo sigue vivo y sin esto seguiría contestando que ya se
// anunció aunque `sessionStorage` haya quedado limpio.
export const resetInviteBannerAnnounced = () => {
    inviteAnnouncedEnMemoria = false

    try {
        sessionStorage.removeItem(INVITE_ANNOUNCED_KEY)
    } catch (e) {
        // sin sessionStorage alcanza con haber bajado el espejo en memoria
    }
}

export const isNewInboxTenant = () => {
    // El override de QA SOLO se honra en no-prod. En prod manda el subdominio, así
    // ningún localStorage en el navegador de un cliente real puede filtrarle la vista
    // nueva a un tenant que todavía no entró en convivencia.
    if (isNonProductionHost()) {
        const forced = readForcedInbox()
        if (forced === 'new') return true
        if (forced === 'legacy') return false
    }
    // Match por subdominio: host exacto (`movistar-ar`) o como primera etiqueta
    // (`movistar-ar.storehub.cloud`, dominios compuestos). NO substring: evita falsos
    // positivos tipo `movistar-ar-x.com` o `claro-movistar-ar...`.
    //
    // El orden importa: legacy puro gana sobre todo, después la convivencia decide con
    // la preferencia del usuario, y el que no está en ninguna de las dos ve la vista
    // nueva. Ese último caso es el default, o sea todo tenant que se dé de alta de acá
    // en adelante sin que nadie toque este archivo.
    const host = window.location.hostname
    if (matchesHost(LEGACY_INBOX_HOSTS, host)) return false
    if (isInboxOptInTenant()) return readInboxViewPreference() === 'new'
    return true
}
